import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import NotesTabsField from './NotesTabsField';

const CHAT_API_BASE = '/api/chat';

// Authenticated fetch wrapper - adds Bearer token to all requests
const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  const headers = {
    ...(options.headers || {}),
    'Authorization': `Bearer ${token}`,
  };
  return fetch(url, { ...options, headers });
};

// Persistent tabbed-notes widget for a parent record (project, room, or task).
// Loads/saves its JSON document via tab-notes.php; autosaves (debounced)
// on change so nothing is lost. NotesTabsField provides @mention support.
const PersistentTabNotes = ({ parentType, parentId, defaultTitle = 'Notes', mentionContext = {} }) => {
  const [value, setValue] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState(null); // 'saving' | 'saved' | null
  const saveTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        const res = await authFetch(
          `${CHAT_API_BASE}/tab-notes.php?parent_type=${encodeURIComponent(parentType)}&parent_id=${encodeURIComponent(parentId)}`
        );
        const data = await res.json();
        if (!cancelled) setValue(data.success ? (data.content || '') : '');
      } catch (e) {
        if (!cancelled) setValue('');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [parentType, parentId]);

  const handleChange = (json) => {
    setValue(json);
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await authFetch(`${CHAT_API_BASE}/tab-notes.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ parent_type: parentType, parent_id: parentId, content: json }),
        });
        setSaveState('saved');
      } catch (e) {
        setSaveState(null);
      }
    }, 600);
  };

  if (!loaded) return <div className="notes-loading">Loading notes...</div>;

  return (
    <div className="tab-notes-block">
      <NotesTabsField value={value} defaultTitle={defaultTitle} onChange={handleChange} rows={6} mentionContext={mentionContext} />
      <div className="tab-notes-savehint">
        {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
      </div>
    </div>
  );
};

export default PersistentTabNotes;
